import { useEffect, useState } from 'react';
import { fetchStoreSettings, updateStoreSettings } from '../../api/appmanageSettings';
import { fetchStoreBranding, updateStoreBranding } from '../../api/appmanageBranding';
import { StoreBranding, StoreSettings } from '../../types/admin';
import './StoreOperationScreen.scss';

function StoreSettingsForm() {
  const [settings, setSettings] = useState<StoreSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchStoreSettings().then(setSettings);
  }, []);

  if (!settings) return <p>불러오는 중...</p>;

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    await updateStoreSettings({
      openTime: settings.open_time,
      closeTime: settings.close_time,
      isTempClosed: !!settings.is_temp_closed,
      tempClosedReason: settings.temp_closed_reason || '',
      defaultWaitMinutes: settings.default_wait_minutes,
      minOrderAmount: settings.min_order_amount,
    });
    setSaving(false);
    setSaved(true);
  };

  return (
    <div className="StoreSettingsForm">
      <div className="StoreSettingsForm-row">
        <label>영업 시작</label>
        <input
          type="time"
          value={settings.open_time.slice(0, 5)}
          onChange={(e) => setSettings({ ...settings, open_time: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>영업 종료</label>
        <input
          type="time"
          value={settings.close_time.slice(0, 5)}
          onChange={(e) => setSettings({ ...settings, close_time: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>기본 대기 시간(분)</label>
        <input
          type="number"
          min={0}
          value={settings.default_wait_minutes}
          onChange={(e) => setSettings({ ...settings, default_wait_minutes: Number(e.target.value) })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>최소 주문 금액(원)</label>
        <input
          type="number"
          min={0}
          step={100}
          value={settings.min_order_amount}
          onChange={(e) => setSettings({ ...settings, min_order_amount: Number(e.target.value) })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>임시 휴무</label>
        <input
          type="checkbox"
          checked={!!settings.is_temp_closed}
          onChange={(e) => setSettings({ ...settings, is_temp_closed: e.target.checked ? 1 : 0 })}
        />
      </div>
      {!!settings.is_temp_closed && (
        <div className="StoreSettingsForm-row">
          <label>휴무 사유</label>
          <input
            type="text"
            value={settings.temp_closed_reason || ''}
            onChange={(e) => setSettings({ ...settings, temp_closed_reason: e.target.value })}
          />
        </div>
      )}
      <button type="button" onClick={handleSave} disabled={saving}>
        {saving ? '저장 중...' : '저장'}
      </button>
      {saved && <span className="StoreSettingsForm-saved">저장되었습니다</span>}
    </div>
  );
}

function BrandingForm() {
  const [branding, setBranding] = useState<StoreBranding | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchStoreBranding().then(setBranding);
  }, []);

  if (!branding) return <p>불러오는 중...</p>;

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    await updateStoreBranding({
      logoUrl: branding.logo_url || '',
      primaryColor: branding.primary_color || '',
      tagline: branding.tagline || '',
      bannerUrl: branding.banner_url || '',
      notice: branding.notice || '',
    });
    setSaving(false);
    setSaved(true);
  };

  return (
    <div className="StoreSettingsForm">
      <div className="StoreSettingsForm-row">
        <label>로고 이미지 URL</label>
        <input
          type="text"
          placeholder="https://..."
          value={branding.logo_url || ''}
          onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>대표 색상</label>
        <input
          type="color"
          value={branding.primary_color || '#111827'}
          onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>한 줄 소개</label>
        <input
          type="text"
          placeholder="예: 매일 아침 갓 로스팅한 원두"
          value={branding.tagline || ''}
          onChange={(e) => setBranding({ ...branding, tagline: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>배너 이미지 URL</label>
        <input
          type="text"
          placeholder="https://..."
          value={branding.banner_url || ''}
          onChange={(e) => setBranding({ ...branding, banner_url: e.target.value })}
        />
      </div>
      <div className="StoreSettingsForm-row">
        <label>공지사항</label>
        <textarea
          placeholder="고객 앱 상단에 표시할 공지사항을 입력하세요"
          value={branding.notice || ''}
          onChange={(e) => setBranding({ ...branding, notice: e.target.value })}
        />
      </div>
      {branding.logo_url ? (
        <img className="BrandingForm-preview" src={branding.logo_url} alt="로고 미리보기" />
      ) : null}
      {branding.banner_url ? (
        <img className="BrandingForm-preview" src={branding.banner_url} alt="배너 미리보기" />
      ) : null}
      <button type="button" onClick={handleSave} disabled={saving}>
        {saving ? '저장 중...' : '저장'}
      </button>
      {saved && <span className="StoreSettingsForm-saved">저장되었습니다</span>}
    </div>
  );
}

function StoreOperationScreen() {
  return (
    <div className="StoreOperationScreen">
      <h1>운영 관리</h1>
      <div className="StoreOperationScreen-columns">
        <section>
          <h2>운영 설정</h2>
          <StoreSettingsForm />
        </section>
        <section>
          <h2>브랜드 설정</h2>
          <BrandingForm />
        </section>
      </div>
    </div>
  );
}

export default StoreOperationScreen;
